import React, { useEffect, useState } from 'react'
import { PreviewArticleCard } from '../cards/PreviewArticleCard'
import { SORT_OPTIONS, SortOption } from 'common/hooks/useArticleTemplate'
import { InputGroupField, SelectField } from 'cwp-components/forms'
import { BsSearch } from 'react-icons/bs'
import { articleType as Article } from '../article'

interface AllArticlesAndBlogsProps {
  updateSearchValue: (searchBValue: string) => void
  sortOption: SortOption
  searchValue: string
  mostViewed: Article[]
  results: Article[]
  hasNext: boolean
  loadMore: () => void
  setSelectedType: (selectedType: string) => void
  setSortOption: (sortOption: SortOption) => void
  isLoading: boolean
  articlesSelected: boolean
  setArticlesSelected: (articlesSelected: boolean) => void
}

export const AllArticlesAndBlogs = ({
  updateSearchValue,
  sortOption,
  searchValue,
  mostViewed,
  results,
  hasNext,
  loadMore,
  setSelectedType,
  setSortOption,
  isLoading,
  articlesSelected,
  setArticlesSelected,
}: AllArticlesAndBlogsProps) => {
  const [filterApplied, setFilterApplied] = useState<boolean>(false)
  const [resetKey, setResetKey] = useState<number>(0)
  const [mostViewedSelected, setMostViewedSelected] = useState(false)

  const clearFilters = () => {
    setFilterApplied(false)
    setResetKey(resetKey + 1)
    updateSearchValue('')
    handleDropDownSelect(SORT_OPTIONS[2].label)
  }

  const handleDropDownSelect = (label) => {
    if (label == 'Most viewed') {
      setMostViewedSelected(true)
    } else {
      setMostViewedSelected(false)
      setSortOption(SORT_OPTIONS.find((filter) => filter.label === label))
    }
  }

  const handleSearchChange = (e: any) => {
    updateSearchValue(e.target.value)
  }

  useEffect(() => {
    setFilterApplied(
      !!searchValue || sortOption != SORT_OPTIONS[2] || mostViewedSelected,
    )
  }, [searchValue, sortOption, mostViewedSelected])

  useEffect(() => {
    setSelectedType(articlesSelected ? 'article' : 'blog')
  }, [articlesSelected])

  return (
    <div className='flex flex-col max-w-[1038px]'>
      <h3 className='font-bold text-lg md:text-2xl lg:text-[40px] text-[#001A3D]'>
        All articles and blogs
      </h3>
      <div className='flex flex-wrap w-full items-center gap-5 pt-[12px] pb-[32px]'>
        <InputGroupField
          placeholder='Type what you are searching for...'
          Icon={BsSearch}
          reverse
          className='flex-1 max-w-[586px] min-w-[300px]'
          onChange={handleSearchChange}
          value={searchValue}
        />

        <div className='flex gap-5'>
          <SelectField
            key={`SelectField_${resetKey}`}
            label={sortOption.label}
            id='sort'
            name='sort'
            records={[
              ...SORT_OPTIONS,
              { label: 'Most viewed', value: 'most_viewed' },
            ]}
            onSelectionChange={handleDropDownSelect}
            className='flex-1 max-w-[250px] min-w-[200px]'
          />

          <button
            onClick={clearFilters}
            disabled={!filterApplied}
            className={`text-xs lg:text-base font-medium text-[#00323D] disabled:text-[#A0ABBE] disabled:cursor-not-allowed`}
          >
            Clear filters
          </button>
        </div>
      </div>

      <div className='flex pb-[5px] gap-[18px] text-[#25395C]'>
        <button
          onClick={() => setArticlesSelected(true)}
          className={`${
            articlesSelected ? 'border-b-[#65C2D7]' : 'border-transparent'
          } border-b-[4px]`}
        >
          Articles
        </button>

        <button
          onClick={() => setArticlesSelected(false)}
          className={`${
            !articlesSelected ? 'border-b-[#65C2D7]' : 'border-transparent'
          } border-b-[4px]`}
        >
          Blogs
        </button>
      </div>

      <div className='flex flex-col justify-center lg:justify-left pt-5'>
        <div className='flex justify-center xl:justify-start flex-wrap gap-6'>
          {isLoading
            ? 'Loading...'
            : (mostViewedSelected ? mostViewed : results).map(
                (article, idx) => (
                  <PreviewArticleCard
                    key={'Preview' + idx}
                    {...article}
                    route={`/articles/${article.id}`}
                  />
                ),
              )}
        </div>

        {hasNext && (
          <div className='flex w-full text-xs lg:text-base font-medium justify-end mt-[20px]'>
            <button onClick={loadMore} className='text-underline'>
              Show more
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
